/// <reference path="./prestige-connector-api.js" />
// @ts-check

class PrestigeProgressRing extends HTMLElement {
  constructor() {
    super();
    /** @type {number|null} */
    this.counterAnimationId = null;
    /** @type {number|null} */
    this.ringAnimationId = null;
    /** @type {number} */
    this.targetCurrentPoints = 0;
    /** @type {number} */
    this.targetNextPoints = 0;
    /** @type {((e: Event) => void)|null} */
    this.handleChange = null;
  }

  connectedCallback() {
    // Get initial values from metafields
    const metafieldPoints = parseInt(this.dataset.points || '0', 10);
    let metafieldNext = parseInt(this.dataset.next || '0', 10);
    if (metafieldPoints > metafieldNext) {
      metafieldNext = metafieldPoints;
    }

    // Set initial targets
    this.targetCurrentPoints = metafieldPoints;
    this.targetNextPoints = metafieldNext;

    // Start animation to metafield values
    this.animateToTarget(true);

    // Subscribe to Antavo updates
    if (window.prestigeStore?.profile) {
      this.handleChange = () => this.handleProfileUpdate();
      window.prestigeStore.profile.addEventListener('change', this.handleChange);
      window.prestigeStore.profile.load();
    }
  }

  disconnectedCallback() {
    this.cancelAnimations();

    if (this.handleChange && window.prestigeStore?.profile) {
      window.prestigeStore.profile.removeEventListener('change', this.handleChange);
      this.handleChange = null;
    }
  }

  cancelAnimations() {
    if (this.counterAnimationId !== null) {
      cancelAnimationFrame(this.counterAnimationId);
      this.counterAnimationId = null;
    }
    if (this.ringAnimationId !== null) {
      cancelAnimationFrame(this.ringAnimationId);
      this.ringAnimationId = null;
    }
  }

  handleProfileUpdate() {
    const profile = window.prestigeStore?.profile;
    if (!profile?.data) return;

    const currentTier = profile.data.tiers?.[0];
    if (!currentTier) return;

    const currentPoints = currentTier.points || 0;
    const nextTier = currentTier.next;
    const hasNextTier = !!nextTier?.points;

    let nextLevelPoints = hasNextTier
      ? currentPoints + nextTier.points
      : currentTier.threshold;

    // Ensure progress never exceeds 100%
    if (currentPoints > nextLevelPoints) {
      nextLevelPoints = currentPoints;
    }

    // Update targets if different
    if (this.targetCurrentPoints !== currentPoints || this.targetNextPoints !== nextLevelPoints) {
      this.targetCurrentPoints = currentPoints;
      this.targetNextPoints = nextLevelPoints;

      // Update the next level points text immediately
      const nextLevelEl = this.querySelector('[data-bind="next-level-points"]');
      if (nextLevelEl) {
        nextLevelEl.textContent = String(nextLevelPoints);
      }

      // Animate to new target (no delay, continues from current position)
      this.animateToTarget(false);
    }
  }

  /**
   * Animate to the current target values
   * @param {boolean} withDelay
   */
  animateToTarget(withDelay) {
    const circle = this.querySelector('.circle-progress');
    const counterEl = this.querySelector('.prestige-counter');

    if (!(circle instanceof SVGCircleElement) || !counterEl) return;

    // Cancel any running animations
    this.cancelAnimations();

    const radius = parseFloat(this.dataset.radius || '16');
    const circumference = 2 * Math.PI * radius;

    const currentPoints = this.targetCurrentPoints;
    const nextTierPoints = this.targetNextPoints > 0 ? this.targetNextPoints : currentPoints;
    const progressRatio = nextTierPoints === 0 ? 0 : currentPoints / nextTierPoints;
    const finalOffset = circumference * (1 - progressRatio);

    // Get current values for smooth transition
    const currentOffset = parseFloat(circle.getAttribute('stroke-dashoffset') || String(circumference));
    const currentCounterValue = parseInt(counterEl.textContent || '0', 10);

    circle.setAttribute('stroke-dasharray', String(circumference));

    // Animate ring
    this.animateValue({
      start: currentOffset,
      end: finalOffset,
      duration: 1500,
      delay: withDelay ? 1000 : 0,
      onUpdate: (val) => {
        circle.setAttribute('stroke-dashoffset', val.toFixed(2));
      },
      onDone: () => {
        this.ringAnimationId = null;
      },
      assignId: (id) => this.ringAnimationId = id
    });

    // Animate counter
    this.animateValue({
      start: currentCounterValue,
      end: currentPoints,
      duration: 1500,
      delay: withDelay ? 1000 : 0,
      onUpdate: (val) => {
        counterEl.textContent = Math.floor(val).toString();
      },
      onDone: () => {
        this.counterAnimationId = null;
      },
      assignId: (id) => this.counterAnimationId = id
    });
  }

  /**
   * Generalized animation function
   * @param {{
   *  start: number,
   *  end: number,
   *  duration: number,
   *  delay?: number,
   *  onUpdate: (val: number) => void,
   *  onDone?: () => void,
   *  assignId?: (id: number) => void
   * }} params
   */
  animateValue({ start, end, duration, delay = 0, onUpdate, onDone, assignId }) {
    const startTime = performance.now();

    /** @param {number} now */
    const step = (now) => {
      const elapsed = now - startTime;

      if (elapsed < delay) {
        onUpdate(start);
        assignId?.(requestAnimationFrame(step));
        return;
      }

      const progress = Math.min((elapsed - delay) / duration, 1);
      const value = start + (end - start) * progress;
      onUpdate(value);

      if (progress < 1) {
        assignId?.(requestAnimationFrame(step));
      } else {
        onDone?.();
      }
    };

    assignId?.(requestAnimationFrame(step));
  }
}

customElements.define('prestige-progress-ring', PrestigeProgressRing);
